import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { Box, Collapse, Stack } from "@mui/material";
import { FormCategory } from "@simplyk/common";

import { useCurrentUserContext } from "../../contexts/CurrentUserContext";
import InviteUserPermissionModal from "../Settings/Modals/UserPermissionModal/InviteUserPermissionModal";

import { ShareFormTabLayout } from "./ShareFormTabLayout";

import Image from "@/components/Image/Image";
import { OpenForm } from "@/components/OpenForm/OpenForm";
import { CloseDialogButton } from "@/components/ShareFormModal/CloseDialogButton";
import { ShareFormTabTitle } from "@/components/ShareFormModal/ShareFormTabTitle";
import { ShareThis } from "@/components/ShareThis/ShareThis";
import { ShareThisButton } from "@/components/ShareThis/types";
import { TextFieldWithCopy } from "@/components/TextFieldWithCopy/TextFieldWithCopy";
import { AmplitudeEvents } from "@/constants/amplitude";
import { DashboardButton } from "@/dashboard-design-system/Button";
import { DashboardChip } from "@/dashboard-design-system/DashboardChip";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { DashboardSelectBar } from "@/dashboard-design-system/SelectBar";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Trans, useTranslate } from "@/hooks/useTranslate";
import { ChevronDown, ChevronUp } from "@/icons/outlined";

type HomeTabProps = {
  mode?: "default" | "form-creation";
  canCollapseAdditionalSharingOptions?: boolean;
  defaultCollapseAdditionalSharingOptions?: boolean;
  onExpandAdditionalSharingOptions?: () => void;
  onCollapseAdditionalSharingOptions?: () => void;
  isTitleByFormCategory?: boolean;
  formCategory?: FormCategory;
  formLink: string;
  optionalActions?: {
    invitationEmail?: {
      mode?: "default" | "giving-tuesday";
      onClick?: () => void;
    };
  };
  onQrCodeActionClick?: () => void;
  onEmbedActionClick?: () => void;
  onClose?: () => void;
  hideViewFormButton?: boolean;
};

export const HomeTab: FC<HomeTabProps> = ({
  hideViewFormButton = false,
  mode = "default",
  canCollapseAdditionalSharingOptions = false,
  defaultCollapseAdditionalSharingOptions = true,
  isTitleByFormCategory = false,
  formCategory,
  formLink,
  optionalActions,
  onQrCodeActionClick,
  onEmbedActionClick,
  onExpandAdditionalSharingOptions,
  onCollapseAdditionalSharingOptions,
  onClose,
}) => {
  const { logAmplitudeEvent } = useAmplitude();
  const { isSmallScreen } = useMediaQuery();
  const { t } = useTranslate();
  const { isOrganizationOnboarded } = useCurrentUserContext();
  const [collapsed, setCollapsed] = useState(
    canCollapseAdditionalSharingOptions ? defaultCollapseAdditionalSharingOptions : false
  );
  const [isUsersManagementModalOpen, setIsUsersManagementModalOpen] = useState(false);

  useEffect(() => {
    if (canCollapseAdditionalSharingOptions) {
      setCollapsed(defaultCollapseAdditionalSharingOptions);
    }
  }, [canCollapseAdditionalSharingOptions, defaultCollapseAdditionalSharingOptions]);

  const title = useMemo(() => {
    if (mode === "form-creation") {
      return <Trans space="dashboard" i18nKey="shareFormModal.title.formIsReady" components={{ br: <br /> }} />;
    }

    if (isTitleByFormCategory) {
      switch (formCategory) {
        case FormCategory.DonationForm:
          return t("dashboard", "shareFormModal.title.donationForm");
        case FormCategory.PeerToPeerCampaign:
          return t("dashboard", "shareFormModal.title.p2p");
        case FormCategory.Event:
          return t("dashboard", "shareFormModal.title.event");
        case FormCategory.Shop:
          return t("dashboard", "shareFormModal.title.shop");
        case FormCategory.MembershipV2:
          return t("dashboard", "shareFormModal.title.membership");
        case FormCategory.Lottery:
          return t("dashboard", "shareFormModal.title.raffle");
        case FormCategory.Auction:
          return t("dashboard", "shareFormModal.title.auction");
        case FormCategory.Custom:
          return t("dashboard", "shareFormModal.title.custom");
        default:
          return <Trans space="dashboard" i18nKey="shareFormModal.title" components={{ br: <br /> }} />;
      }
    }

    return <Trans space="dashboard" i18nKey="shareFormModal.title" components={{ br: <br /> }} />;
  }, [mode, isTitleByFormCategory, formCategory, t]);

  const [linkCopied, setLinkCopied] = useState(false);
  const handleOnCopy = useCallback(() => {
    setLinkCopied(true);
  }, []);

  const handleOpenFormClick = useCallback(() => {
    logAmplitudeEvent(AmplitudeEvents.ShareFormModalClickSeeForm);
  }, [logAmplitudeEvent]);

  const handleCollapseAdditionalSharingOptions = useCallback(() => {
    setCollapsed(true);
    onCollapseAdditionalSharingOptions?.();
  }, [onCollapseAdditionalSharingOptions]);

  const handleExpandAdditionalSharingOptions = useCallback(() => {
    setCollapsed(false);
    onExpandAdditionalSharingOptions?.();
  }, [onExpandAdditionalSharingOptions]);

  const handleInviteTeamMember = useCallback(() => {
    logAmplitudeEvent(AmplitudeEvents.InviteUserDuringActivationClicked, { user_type: "Team Member" });
    setIsUsersManagementModalOpen(true);
  }, [logAmplitudeEvent]);

  const teamIllustration = "/images/invite-team-member.svg";
  const organizationIsOnboarded = Boolean(isOrganizationOnboarded);

  return (
    <>
      <ShareFormTabLayout
        withConfetti={mode === "form-creation"}
        slots={{
          closeButton: <CloseDialogButton onClick={onClose} />,
          header: (
            <Stack width="100%" alignItems="center" gap={3}>
              <Stack alignItems="center" gap={0.5}>
                <ShareFormTabTitle>{title}</ShareFormTabTitle>
                {isTitleByFormCategory && (
                  <DashboardTypography variant="body1" vibe="text-reverse-intense" textAlign="center">
                    {t("dashboard", "shareFormModal.subtitle")}
                  </DashboardTypography>
                )}
              </Stack>
              <Box width="100%">
                <TextFieldWithCopy
                  value={formLink}
                  button
                  onCopy={handleOnCopy}
                  size="medium"
                  buttonCopyProps={{
                    size: "medium",
                    copyText: t("dashboard", "shareFormModal.copyLinkButtonLabel"),
                  }}
                  data-test="share-form-modal-link"
                  copyAmplitudeEvent={AmplitudeEvents.FormSharingClicked}
                  copyAmplitudePayload={{ social_platform: "Copy Link", form_type: formCategory }}
                />
                {linkCopied && (
                  <DashboardInfobox
                    hideIcon
                    vibe="brand"
                    type="alert"
                    pointer="top-left"
                    size="medium"
                    title={t("dashboard", "shareFormModal.infoBox.title")}
                    slots={{
                      right: (
                        <Image
                          width={98}
                          height={undefined}
                          alt="share-icons"
                          src="/images/shareFormModal/share-icons.svg"
                        />
                      ),
                    }}
                  >
                    {t("dashboard", "shareFormModal.infoBox.content")}
                  </DashboardInfobox>
                )}
              </Box>
              <ShareThis
                buttonSize={40}
                spaceBetweenButton={2}
                noMargin
                buttons={[ShareThisButton.Facebook, ShareThisButton.Messenger, ShareThisButton.Email]}
                url={formLink}
                formCategory={formCategory ? FormCategory[formCategory] : null}
              />
            </Stack>
          ),
          content: (
            <Stack alignItems="center">
              {canCollapseAdditionalSharingOptions && (
                <DashboardButton
                  vibe="neutral"
                  endIcon={collapsed ? <ChevronDown /> : <ChevronUp />}
                  onClick={collapsed ? handleExpandAdditionalSharingOptions : handleCollapseAdditionalSharingOptions}
                >
                  {collapsed
                    ? t("dashboard", "shareFormModal.moreWaysToShare")
                    : t("dashboard", "shareFormModal.lessWaysToShare")}
                </DashboardButton>
              )}
              <Collapse in={!collapsed}>
                <Stack gap={2} paddingBlockStart={canCollapseAdditionalSharingOptions ? 3 : 0}>
                  {!organizationIsOnboarded && (
                    <DashboardSelectBar
                      title={t("dashboard", "shareFormModal.choice.inviteTeamMember.title")}
                      description={t("dashboard", "shareFormModal.choice.inviteTeamMember.description")}
                      onClick={handleInviteTeamMember}
                      illustration={teamIllustration}
                    />
                  )}
                  {optionalActions?.invitationEmail && (
                    <DashboardSelectBar
                      title={
                        <Stack direction="row" alignItems="center" gap={1}>
                          <DashboardTypography variant="subtitle1">
                            {optionalActions.invitationEmail.mode === "giving-tuesday"
                              ? t("dashboard", "shareFormModal.choice.givingTuesday.invitationEmail.title")
                              : t("dashboard", "shareFormModal.choice.invitationEmail.title")}
                          </DashboardTypography>
                          <DashboardChip
                            variant="outlined"
                            vibe="positive"
                            label={t("dashboard", "shareFormModal.choice.invitationEmail.title.chip")}
                          />
                        </Stack>
                      }
                      description={
                        optionalActions.invitationEmail.mode === "giving-tuesday"
                          ? t("dashboard", "shareFormModal.choice.givingTuesday.invitationEmail.description")
                          : t("dashboard", "shareFormModal.choice.invitationEmail.description")
                      }
                      onClick={optionalActions.invitationEmail.onClick}
                    />
                  )}
                  <DashboardSelectBar
                    title={t("dashboard", "shareFormModal.choice.qrCode.title")}
                    description={t("dashboard", "shareFormModal.choice.qrCode.description")}
                    onClick={onQrCodeActionClick}
                  />
                  <DashboardSelectBar
                    title={t("dashboard", "shareFormModal.choice.embed.title")}
                    description={t("dashboard", "shareFormModal.choice.embed.description")}
                    onClick={onEmbedActionClick}
                  />
                </Stack>
              </Collapse>
            </Stack>
          ),
          footer: isSmallScreen && !hideViewFormButton && (
            <Stack direction="row" alignItems="center" justifyContent="flex-end">
              <OpenForm link={formLink} vibe="brand" variant="filled" onClick={handleOpenFormClick} />
            </Stack>
          ),
        }}
      />
      {isUsersManagementModalOpen && (
        <InviteUserPermissionModal
          isOpen={isUsersManagementModalOpen}
          onClose={() => setIsUsersManagementModalOpen(false)}
          source="share-modal"
          formLink={formLink}
        />
      )}
    </>
  );
};
